import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { APP_NAME } from '../config';
import { formatDateDisplay } from './dates';

/**
 * Single source of truth for our PDF report chrome.
 *
 * Every Pediatrack PDF — Growth Monitoring, Vaccination Status,
 * Child Registration, Profiles export, the per-child vaccination
 * and growth history pages — renders the same header band, the
 * same filter strip, the same grid table style, and the same
 * "page X of Y" footer. Callers only own the section data.
 *
 * Usage:
 *   buildPdfReport({
 *     title: 'Growth Monitoring Summary Report',
 *     subtitle: `${APP_NAME} · Total Children: 32`,
 *     filters: ['Growth Status: Underweight', 'Search: "x"'],
 *     sections: [{
 *       heading: 'Action Queue',
 *       head: ['Reg No', 'Child Name', 'Status'],
 *       body: rows,
 *       accent: [239, 68, 68],   // optional, defaults to indigo
 *     }],
 *     fileName: 'Growth_Monitoring_Report.pdf',
 *     toast,                    // useToast() handle
 *   });
 *
 * For per-child PDFs that draw charts above the table (Growth.jsx),
 * use `runPdfTable` instead — it lets callers prep a doc with custom
 * artwork first, then drops in the standard autoTable + footer.
 */

const SLATE_900 = [15, 23, 42];
const SLATE_500 = [100, 116, 139];
const DEFAULT_ACCENT = [79, 70, 229]; // indigo-600
const ZEBRA = [249, 250, 251];        // slate-50
const SECTION_BG = [241, 245, 249];   // slate-100

/**
 * @param {{
 *   title: string,
 *   subtitle?: string,
 *   filters?: string[],
 *   sections: Array<{
 *     heading?: string,
 *     head: string[],
 *     body: any[][],
 *     accent?: [number, number, number],
 *   }>,
 *   fileName: string,
 *   toast?: { success: (msg: string) => void, warning?: (msg: string) => void },
 *   orientation?: 'landscape'|'portrait',
 *   emptyMessage?: string,
 * }} opts
 */
export function buildPdfReport(opts) {
    const {
        title,
        subtitle = '',
        filters = [],
        sections,
        fileName,
        toast,
        orientation = 'landscape',
        emptyMessage = 'Nothing to export yet.',
    } = opts;

    if (!sections || sections.length === 0 || sections.every((s) => !s.body || s.body.length === 0)) {
        if (toast?.warning) toast.warning(emptyMessage);
        else console.warn(emptyMessage);
        return;
    }

    const doc = new jsPDF(orientation, 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 14;
    let y = 18;

    // ── Header band ─────────────────────────────────────────────────────
    doc.setFontSize(20);
    doc.setTextColor(...SLATE_900);
    doc.text(title, margin, y);
    y += 7;

    if (subtitle) {
        doc.setFontSize(11);
        doc.setTextColor(...SLATE_500);
        doc.text(subtitle, margin, y);
        y += 6;
    }
    doc.setFontSize(9);
    doc.setTextColor(...SLATE_500);
    doc.text(
        `Generated ${formatDateDisplay(new Date().toISOString())} ${new Date().toLocaleTimeString()} · ${APP_NAME}`,
        margin,
        y,
    );
    y += 6;

    // ── Filter strip ────────────────────────────────────────────────────
    y = drawFilterStrip(doc, filters, margin, y, pageW);

    // ── Sections ────────────────────────────────────────────────────────
    // autoTable's default bottom margin is too conservative — it pushed even
    // a moderate Reminders queue (12 rows) onto page 2 with a blank page 1
    // (the bug the user reported). Setting bottom: 0 lets autoTable use the
    // full page height up to the footer band; combined with the slimmed-
    // down inline filter strip above, a 12-row queue fits cleanly on page 1
    // with lastY ≈ 193. The pre-break heuristic reserves the same band so
    // when it triggers the section genuinely won't fit and the break is
    // intentional (heading + first few rows on page 1, rest on page 2).
    const TABLE_BOTTOM_MARGIN = 0;
    const FOOTER_BAND = 8; // matches drawFooter: label sits at pageH - 8
    const SAFE_BOTTOM = FOOTER_BAND + TABLE_BOTTOM_MARGIN;
    sections.forEach((section, i) => {
        const sectionH = estimateSectionHeight(section);
        if (y + sectionH > pageH - SAFE_BOTTOM && y > 30) {
            doc.addPage();
            y = 18;
        }

        if (section.heading) {
            doc.setFontSize(14);
            doc.setTextColor(...SLATE_900);
            doc.text(section.heading, margin, y);
            y += 6;
        }

        autoTable(doc, {
            head: [section.head],
            body: section.body,
            startY: y,
            theme: 'grid',
            headStyles: {
                fillColor: section.accent || DEFAULT_ACCENT,
                textColor: 255,
                fontStyle: 'bold',
                halign: 'left',
            },
            styles: {
                fontSize: 10,
                cellPadding: 3.5,
                textColor: SLATE_900,
                lineColor: [226, 232, 240], // slate-200
                lineWidth: 0.1,
            },
            alternateRowStyles: { fillColor: ZEBRA },
            margin: { left: margin, right: margin, bottom: SAFE_BOTTOM },
            didDrawPage: () => drawFooter(doc, pageW, pageH, margin),
        });

        // autoTable leaves its final Y in `doc.lastAutoTable.finalY`.
        y = (doc.lastAutoTable?.finalY || y) + 8;

        // Thicker rule between sections for visual separation.
        if (i < sections.length - 1) {
            doc.setDrawColor(...SECTION_BG);
            doc.setLineWidth(0.4);
            doc.line(margin, y - 4, pageW - margin, y - 4);
        }
    });

    // Redraw footer on page 1 (didDrawPage also covers it but ensures
    // first page always shows it even if the report fits on one page
    // without triggering a redraw — autoTable handles this itself, but
    // being explicit is cheap).
    drawFooter(doc, pageW, pageH, margin);

    doc.save(fileName);
    if (toast?.success) toast.success('PDF exported.');
}

/** Filter strip: label/value pairs rendered as inline text.
 *  Was an autoTable previously; converted to inline text to save ~9mm of
 *  vertical space on page 1 — the table form's head row + cellPadding
 *  consumed enough that a 12-row reminders queue overflowed to page 2
 *  with a blank page 1. Inline text reads as a single labeled line per
 *  filter, which fits the use-case (a few short scope/date strings). */
function drawFilterStrip(doc, filters, margin, y, pageW) {
    if (!filters || filters.length === 0) {
        // No filters provided — skip the strip entirely rather than
        // printing a generic "All registered children" fallback that
        // doesn't reflect the actual scope of every report.
        return y;
    }
    doc.setFontSize(9);
    doc.setTextColor(...SLATE_500);
    const lineH = 4.2;
    const colW = 50;
    filters.forEach((line) => {
        const idx = line.indexOf(':');
        const label = idx >= 0 ? line.slice(0, idx + 1) : 'Filter';
        const value = idx >= 0 ? line.slice(idx + 1).trim() : line;
        doc.setFont(undefined, 'bold');
        doc.text(label, margin, y);
        doc.setFont(undefined, 'normal');
        doc.text(value, margin + colW, y);
        y += lineH;
    });
    return y + 2;
}

function estimateSectionHeight(section) {
    // Heading (~7mm if present) + table head row (~10mm) + ~10.6mm per body row.
    // 10pt font with 3.5mm cellPadding top+bottom ≈ 10.6mm per row in jsPDF
    // (measured: 12 rows = 133mm including head + heading). AutoTable's own
    // pagination handles overflow if we underestimate; this heuristic gates
    // the "page-break BEFORE the heading" call so the heading doesn't get
    // orphaned at the bottom of a page when the table will overflow. Slightly
    // tight so a borderline-fitting table attempts page 1 instead of being
    // conservatively pushed to page 2 (the bug the Reminders export hit).
    const rows = section.body?.length || 0;
    return (section.heading ? 7 : 0) + 10 + rows * 10.6;
}

function drawFooter(doc, pageW, pageH, margin) {
    const page = doc.internal.getNumberOfPages();
    for (let i = 1; i <= page; i += 1) {
        doc.setPage(i);
        doc.setFontSize(9);
        doc.setTextColor(...SLATE_500);
        const label = `Page ${i} of ${page}`;
        doc.text(label, pageW - margin, pageH - 8, { align: 'right' });
        doc.text(APP_NAME, margin, pageH - 8);
    }
}

/**
 * Low-level helper for per-child PDFs (Vaccination.jsx, Growth.jsx)
 * where the caller has drawn custom artwork — per-child metadata
 * block, chart PNGs, etc. — above the autoTable. Apply the same
 * consistent grid table style and the same Page X of Y footer.
 *
 * Caller owns the doc instance, header, page-breaks, and chart
 * drawing; we only own the table + footer.
 *
 * @param {{
 *   doc: import('jspdf').jsPDF,
 *   startY: number,
 *   head: string[],
 *   body: any[][],
 *   accent?: [number, number, number],
 * }} opts
 */
export function runPdfTable({ doc, startY, head, body, accent }) {
    autoTable(doc, {
        head: [head],
        body,
        startY,
        theme: 'grid',
        headStyles: {
            fillColor: accent || DEFAULT_ACCENT,
            textColor: 255,
            fontStyle: 'bold',
            halign: 'left',
        },
        styles: {
            fontSize: 10,
            cellPadding: 3.5,
            textColor: SLATE_900,
            lineColor: [226, 232, 240],
            lineWidth: 0.1,
        },
        alternateRowStyles: { fillColor: ZEBRA },
        margin: { left: 14, right: 14, bottom: 8 },
        didDrawPage: () => {
            const w = doc.internal.pageSize.getWidth();
            const h = doc.internal.pageSize.getHeight();
            drawFooter(doc, w, h, 14);
        },
    });
    // One final pass after the table to make sure the *current* page
    // count is reflected ("Page X of Y" depends on numberOfPages).
    const w = doc.internal.pageSize.getWidth();
    const h = doc.internal.pageSize.getHeight();
    const pages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pages; i += 1) {
        doc.setPage(i);
        doc.setFontSize(9);
        doc.setTextColor(...SLATE_500);
        doc.text(`Page ${i} of ${pages}`, w - 14, h - 8, { align: 'right' });
        doc.text(APP_NAME, 14, h - 8);
    }
}